/**
 * PdfPaperBlocks — on-screen "paper" blocks shared by the PDF export previews.
 * Same DOM structure and class names as the server builders in
 * functions/pdfDocumentBlocks.js; typography from .ck-form-doc
 * (styles/formDocument.css) + the `.pdx-paper` rules in pdfExportShell.css.
 * Pagination is not simulated.
 */

import { Warning } from "@phosphor-icons/react";

export const Bdi = ({ children }) => <bdi>{children}</bdi>;

export function Paper({ children, className = "" }) {
  return (
    <div className={`pdx-paper ${className}`} dir="rtl" aria-label="תצוגה מקדימה של המסמך">
      {children}
    </div>
  );
}

export function PaperLetterhead({ letterhead }) {
  return (
    <header className="letterhead">
      <div className="letterhead-identity">
        {letterhead.logoUrl && (
          <img
            className={`letterhead-logo ${letterhead.logoLarge ? "letterhead-logo--large" : ""}`}
            src={letterhead.logoUrl}
            alt=""
          />
        )}
        {(letterhead.clinicName || letterhead.authorName) && (
          <div>
            {letterhead.clinicName && <div className="letterhead-clinic">{letterhead.clinicName}</div>}
            {letterhead.authorName && (
              <div className="letterhead-therapist">{letterhead.authorName}</div>
            )}
          </div>
        )}
      </div>
      <div className="letterhead-meta">
        {letterhead.metaLines.map((line, i) => (
          <span key={i} className={`lh-line ${line.ltr ? "lh-contact" : ""}`}>
            {line.label ? `${line.label}: ` : ""}
            {line.bdi ? <Bdi>{line.text}</Bdi> : line.text}
          </span>
        ))}
      </div>
    </header>
  );
}

export function PaperAddressee({ text }) {
  if (!text) return null;
  return <p className="doc-addressee">לכבוד: {text}</p>;
}

export function PaperTitle({ text }) {
  return <h1 className="doc-title">{text}</h1>;
}

export function PaperPatientMeta({ rows }) {
  if (!rows?.length) return null;
  return (
    <section className="patient-meta">
      {rows.map((r) => (
        <div className="pm-row" key={r.label}>
          <span className="pm-label">{r.label}:</span>
          <span className="pm-value">{r.bdi ? <Bdi>{r.value}</Bdi> : r.value}</span>
        </div>
      ))}
    </section>
  );
}

export function PaperChips({ chips, className = "doc-chips" }) {
  if (!chips?.length) return null;
  return (
    <div className={className}>
      {chips.map((c, i) => (
        <span key={i} className={`doc-chip ${c.className || ""}`}>
          {c.text}
        </span>
      ))}
    </div>
  );
}

export function PaperEmpty({ children }) {
  return (
    <div className="pdx-paper-empty">
      <Warning size={16} weight="bold" />
      {children}
    </div>
  );
}

export function PaperSignature({ signature }) {
  if (signature.mode === "saved" && signature.imageUrl) {
    return (
      <div className="summary-signature-wrap">
        <div className="therapist-signature-block">
          <img className="therapist-signature-img" src={signature.imageUrl} alt="חתימת מטפל" />
          <span className="therapist-signature-label">חתימת המטפל/ת</span>
          {signature.exporterName && (
            <span className="therapist-signature-name">{signature.exporterName}</span>
          )}
          {signature.licenseNumber && (
            <span className="therapist-signature-meta">
              מס' רישיון: <Bdi>{signature.licenseNumber}</Bdi>
            </span>
          )}
          <span className="therapist-signature-meta">
            תאריך: <Bdi>{signature.generatedAt}</Bdi>
          </span>
        </div>
      </div>
    );
  }
  if (signature.mode === "saved") {
    return (
      <div className="summary-signature-wrap">
        <div className="therapist-signature-block pdx-sig-missing">
          <span className="therapist-signature-label">
            אין חתימה שמורה — תתבקש/י לחתום בלחיצה על ייצוא
          </span>
        </div>
      </div>
    );
  }
  if (signature.mode === "box") {
    return (
      <div className="sign-box">
        <div className="sb-cell">
          <span className="sb-prefill">{signature.exporterName}</span>
          <span className="sb-line" />
          שם המטפל/ת
        </div>
        <div className="sb-cell">
          <span className="sb-prefill" />
          <span className="sb-line" />
          תאריך
        </div>
        <div className="sb-cell">
          <span className="sb-prefill" />
          <span className="sb-line" />
          חתימה
        </div>
      </div>
    );
  }
  return (
    <p className="summary-issued-by">
      הופק על ידי {signature.exporterName || signature.clinicName}
      {signature.licenseNumber ? (
        <>
          {" "}· מס' רישיון <Bdi>{signature.licenseNumber}</Bdi>
        </>
      ) : null}{" "}
      · <Bdi>{signature.generatedAt}</Bdi>
    </p>
  );
}

export function PaperFootnote({ text }) {
  if (!text) return null;
  return (
    <section className="notes-footnote">
      <p>{text}</p>
    </section>
  );
}

export function PaperFooterStrip() {
  return (
    <div className="pdx-paper-footer" aria-hidden="true">
      <span className="pdx-paper-footer-brand">
        <img src="/logo192.png" alt="" />
        <b>CliniKit</b> · clinikit.co.il · office@clinikit.co.il
      </span>
      <span>עמוד 1 מתוך 1</span>
    </div>
  );
}
